import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'

const INCIDENT_TYPES = [
  { value: 'nakal', label: '📋 Nakal / Cheating', color: '#E24B4A' },
  { value: 'badtameezi', label: '😤 Badtameezi / Disrespect', color: '#F97316' },
  { value: 'uniform', label: '👕 Uniform nahi pehni', color: '#EAB308' },
  { value: 'absenteeism', label: '🚫 Bina bataye absent', color: '#8B5CF6' },
  { value: 'phone', label: '📱 Class mein phone use', color: '#06B6D4' },
  { value: 'fight', label: '👊 Ladai / Fighting', color: '#DC2626' },
  { value: 'ragging', label: '⚠️ Ragging', color: '#991B1B' },
  { value: 'late', label: '⏰ Baar baar late aana', color: '#D97706' },
  { value: 'other', label: '📌 Anya / Other', color: '#6B7280' }
]

const ACTIONS = ['Warning di gayi', 'Principal ke paas bheja', 'Parents bulaye', 'Fine lagaya gaya', 'Suspension kiya', 'TC diya', 'Koi action nahi']
const SEVERITY = [{ v: 'low', l: 'Halka ⚡', c: '#D97706' }, { v: 'medium', l: 'Madhyam ⚠️', c: '#7C3AED' }, { v: 'high', l: 'Gambhir 🚨', c: '#DC2626' }]
const PIE_COLORS = ['#E24B4A', '#F97316', '#EAB308', '#8B5CF6', '#06B6D4', '#DC2626', '#991B1B', '#D97706', '#6B7280']

export default function DisciplineTrack({ user }) {
  const [tab, setTab] = useState('dashboard')
  const [students, setStudents] = useState([])
  const [incidents, setIncidents] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [selectedStudent, setSelectedStudent] = useState(null)
  const printRef = useRef()

  const [form, setForm] = useState({ studentName: '', studentClass: '', rollNo: '', parentPhone: '', incidentType: 'nakal', severity: 'medium', description: '', action: 'Warning di gayi', notifyParent: 'yes', fineAmount: '' })
  const [sForm, setSForm] = useState({ name: '', class: '', rollNo: '', parentPhone: '', parentEmail: '' })

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    const { data: inc } = await supabase.from('incidents').select('*, students(name, class, roll_no, parent_phone)').order('created_at', { ascending: false })
    const { data: stu } = await supabase.from('students').select('*').order('name')
    if (inc) setIncidents(inc)
    if (stu) setStudents(stu)
    setLoading(false)
  }

  const recordIncident = async () => {
    if (!form.studentName.trim() || !form.description.trim()) { alert('Student naam aur detail zaroori hai!'); return }
    setSaving(true)
    let studentId = null
    const existing = students.find(s => s.name.toLowerCase() === form.studentName.toLowerCase())
    if (existing) {
      studentId = existing.id
    } else {
      const { data } = await supabase.from('students').insert({ name: form.studentName, class: form.studentClass, roll_no: form.rollNo, parent_phone: form.parentPhone, professor_id: user.id }).select().single()
      if (data) studentId = data.id
    }
    if (studentId) {
      const desc = form.fineAmount ? `${form.description} [Fine: ₹${form.fineAmount}]` : form.description
      await supabase.from('incidents').insert({ student_id: studentId, professor_id: user.id, incident_type: form.incidentType, severity: form.severity, description: desc, action_taken: form.action, parents_notified: form.notifyParent === 'yes' })
    }
    setForm({ ...form, studentName: '', studentClass: '', rollNo: '', parentPhone: '', description: '', fineAmount: '' })
    await loadData()
    setSaving(false)
    alert('✅ Incident record ho gaya!' + (form.notifyParent === 'yes' ? '\n📞 Parents ko call karein aur inform karein.' : ''))
  }

  const addStudent = async () => {
    if (!sForm.name || !sForm.class) { alert('Naam aur class zaroori hai!'); return }
    setSaving(true)
    await supabase.from('students').insert({ name: sForm.name, class: sForm.class, roll_no: sForm.rollNo, parent_phone: sForm.parentPhone, parent_email: sForm.parentEmail, professor_id: user.id })
    setSForm({ name: '', class: '', rollNo: '', parentPhone: '', parentEmail: '' })
    await loadData()
    setSaving(false)
    alert('✅ Student add ho gaya!')
  }

  const deleteIncident = async (id) => {
    if (!window.confirm('Yeh incident delete karna chahte hain?')) return
    await supabase.from('incidents').delete().eq('id', id)
    await loadData()
  }

  const printReport = () => window.print()

  // Analytics
  const studentCounts = {}
  incidents.forEach(i => { const n = i.students?.name || '?'; studentCounts[n] = (studentCounts[n] || 0) + 1 })
  const warningStudents = Object.entries(studentCounts).filter(([, c]) => c >= 3)
  const todayInc = incidents.filter(i => new Date(i.created_at).toDateString() === new Date().toDateString())
  const uniformInc = incidents.filter(i => i.incident_type === 'uniform')
  const highInc = incidents.filter(i => i.severity === 'high')

  const typeChartData = INCIDENT_TYPES.map(t => ({ name: t.label.split(' ').slice(1).join(' '), count: incidents.filter(i => i.incident_type === t.value).length })).filter(d => d.count > 0)
  const weekData = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i))
    return { day: d.toLocaleDateString('hi-IN', { weekday: 'short' }), count: incidents.filter(inc => new Date(inc.created_at).toDateString() === d.toDateString()).length }
  })

  const filteredInc = incidents.filter(i => {
    const name = i.students?.name?.toLowerCase() || ''
    const matchSearch = name.includes(search.toLowerCase()) || i.description?.toLowerCase().includes(search.toLowerCase())
    const matchType = filterType === 'all' || i.incident_type === filterType
    return matchSearch && matchType
  })

  const typeLabel = v => INCIDENT_TYPES.find(t => t.value === v)?.label || v
  const sevInfo = v => SEVERITY.find(s => s.v === v) || SEVERITY[1]

  const tabs = [
    { id: 'dashboard', icon: '📊', label: 'Dashboard' },
    { id: 'record', icon: '📝', label: 'Record' },
    { id: 'history', icon: '📋', label: 'History' },
    { id: 'students', icon: '👥', label: 'Students' },
    { id: 'report', icon: '📄', label: 'Report' }
  ]

  return (
    <div style={{ minHeight: '100vh', background: '#f8f7ff' }}>
      {/* Navbar */}
      <div style={{ background: 'linear-gradient(135deg, #A32D2D, #E24B4A)', padding: '0 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 58, boxShadow: '0 2px 12px rgba(162,45,45,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🛡️</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>DisciplineTrack</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>College Discipline Manager</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>🎓 {user?.user_metadata?.name || user?.email}</span>
          <button onClick={() => supabase.auth.signOut()} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer' }}>Logout</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e8e6ff', padding: '0 1rem', display: 'flex', gap: 4, overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '12px 16px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, background: 'transparent', color: tab === t.id ? '#E24B4A' : '#888', borderBottom: tab === t.id ? '2px solid #E24B4A' : '2px solid transparent', whiteSpace: 'nowrap' }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem 1rem' }}>

        {/* DASHBOARD TAB */}
        {tab === 'dashboard' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Total Incidents', val: incidents.length, color: '#A32D2D', icon: '📋' },
                { label: 'Aaj ke Incidents', val: todayInc.length, color: '#D97706', icon: '📅' },
                { label: 'Warning Zone', val: warningStudents.length, color: '#E24B4A', icon: '⚠️' },
                { label: 'Gambhir Cases', val: highInc.length, color: '#DC2626', icon: '🚨' },
                { label: 'Uniform Violation', val: uniformInc.length, color: '#7C3AED', icon: '👕' },
                { label: 'Total Students', val: students.length, color: '#059669', icon: '👥' }
              ].map(s => (
                <div key={s.label} style={{ background: '#fff', border: '1px solid #e8e6ff', borderRadius: 14, padding: '1rem', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>{s.icon}</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.val}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {warningStudents.length > 0 && (
              <div style={{ background: '#fff', border: '1px solid #fca5a5', borderRadius: 14, padding: '1rem', marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#A32D2D', marginBottom: 10 }}>🚨 Warning Zone — Ye students 3+ incidents mein hain!</div>
                {warningStudents.map(([name, count]) => (
                  <div key={name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#FCEBEB', borderRadius: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#A32D2D' }}>⚠️ {name}</span>
                    <span style={{ fontSize: 12, background: '#A32D2D', color: '#fff', padding: '2px 10px', borderRadius: 20 }}>{count} incidents</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
              <div style={{ background: '#fff', border: '1px solid #e8e6ff', borderRadius: 14, padding: '1rem' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 14 }}>📈 Pichhle 7 din ke incidents</div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={weekData}>
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#E24B4A" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {typeChartData.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #e8e6ff', borderRadius: 14, padding: '1rem' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 14 }}>🥧 Incident types breakdown</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={typeChartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name.slice(0, 8)} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                        {typeChartData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        )}

        {/* RECORD TAB */}
        {tab === 'record' && (
          <div style={{ background: '#fff', border: '1px solid #e8e6ff', borderRadius: 16, padding: '1.5rem' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#A32D2D', marginBottom: 18 }}>⚠️ Naya Incident Record Karo</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={lbl}>Student ka naam *</label>
                <input list="sList" value={form.studentName} onChange={e => setForm({ ...form, studentName: e.target.value })} placeholder="Naam likhein ya chuno" style={inp} />
                <datalist id="sList">{students.map(s => <option key={s.id} value={s.name} />)}</datalist>
              </div>
              <div>
                <label style={lbl}>Class / Section</label>
                <input value={form.studentClass} onChange={e => setForm({ ...form, studentClass: e.target.value })} placeholder="BA 2nd Year, Sec A" style={inp} />
              </div>
              <div>
                <label style={lbl}>Roll No</label>
                <input value={form.rollNo} onChange={e => setForm({ ...form, rollNo: e.target.value })} placeholder="2024001" style={inp} />
              </div>
              <div>
                <label style={lbl}>Parent Phone</label>
                <input value={form.parentPhone} onChange={e => setForm({ ...form, parentPhone: e.target.value })} placeholder="98765XXXXX" style={inp} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={lbl}>Incident ka prakar *</label>
                <select value={form.incidentType} onChange={e => setForm({ ...form, incidentType: e.target.value })} style={inp}>
                  {INCIDENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Severity / Gambhirta</label>
                <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })} style={inp}>
                  {SEVERITY.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Action liya gaya</label>
                <select value={form.action} onChange={e => setForm({ ...form, action: e.target.value })} style={inp}>
                  {ACTIONS.map(a => <option key={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Fine amount (agar laga ho)</label>
                <input value={form.fineAmount} onChange={e => setForm({ ...form, fineAmount: e.target.value })} placeholder="₹ amount" style={inp} />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Incident ki poori detail *</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={4} placeholder="Kya hua, kab hua, kaun kaun witness tha — sab detail mein likhein..." style={{ ...inp, resize: 'vertical' }} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={lbl}>Parents ko notify karein?</label>
              <select value={form.notifyParent} onChange={e => setForm({ ...form, notifyParent: e.target.value })} style={{ ...inp, maxWidth: 300 }}>
                <option value="yes">✅ Haan — notify karein</option>
                <option value="no">❌ Nahi — abhi nahi</option>
              </select>
            </div>

            <button onClick={recordIncident} disabled={saving} style={{ width: '100%', padding: '13px 0', borderRadius: 12, border: 'none', background: saving ? '#ccc' : 'linear-gradient(135deg, #E24B4A, #A32D2D)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? '⏳ Save ho raha hai...' : '⚠️ Incident Record Karo'}
            </button>
          </div>
        )}

        {/* HISTORY TAB */}
        {tab === 'history' && (
          <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Student naam ya description search karein..." style={{ ...inp, maxWidth: 320, marginBottom: 0 }} />
              <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...inp, maxWidth: 200, marginBottom: 0 }}>
                <option value="all">Sab types</option>
                {INCIDENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div style={{ background: '#fff', border: '1px solid #e8e6ff', borderRadius: 16, padding: '1.25rem' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#A32D2D', marginBottom: 14 }}>📋 Incidents History ({filteredInc.length})</div>
              {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Loading...</div>}
              {!loading && filteredInc.length === 0 && <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Koi incident nahi mila</div>}
              {filteredInc.map(inc => (
                <div key={inc.id} style={{ borderLeft: `4px solid ${INCIDENT_TYPES.find(t => t.value === inc.incident_type)?.color || '#E24B4A'}`, padding: '10px 14px', marginBottom: 12, background: '#fafafe', borderRadius: '0 10px 10px 0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#222', cursor: 'pointer' }} onClick={() => setSelectedStudent(inc.students?.name)}>
                      👤 {inc.students?.name || '?'} {inc.students?.class ? `· ${inc.students.class}` : ''}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: '#FCEBEB', color: '#A32D2D', fontWeight: 600 }}>{typeLabel(inc.incident_type)}</span>
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: sevInfo(inc.severity).c + '20', color: sevInfo(inc.severity).c, fontWeight: 600 }}>{sevInfo(inc.severity).l}</span>
                      <button onClick={() => deleteIncident(inc.id)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#E24B4A', cursor: 'pointer' }}>🗑️</button>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: '#444', lineHeight: 1.6 }}>{inc.description}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>✅ {inc.action_taken} · {inc.parents_notified ? '📞 Parents notified' : 'Parents notify nahi'} · 🕐 {new Date(inc.created_at).toLocaleDateString('hi-IN')} {new Date(inc.created_at).toLocaleTimeString('hi-IN')}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STUDENTS TAB */}
        {tab === 'students' && (
          <div>
            <div style={{ background: '#fff', border: '1px solid #e8e6ff', borderRadius: 16, padding: '1.25rem', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#A32D2D', marginBottom: 16 }}>➕ Naya Student Add Karo</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                <div><label style={lbl}>Naam *</label><input value={sForm.name} onChange={e => setSForm({ ...sForm, name: e.target.value })} placeholder="Rahul Sharma" style={inp} /></div>
                <div><label style={lbl}>Class *</label><input value={sForm.class} onChange={e => setSForm({ ...sForm, class: e.target.value })} placeholder="BA 2nd Year" style={inp} /></div>
                <div><label style={lbl}>Roll No</label><input value={sForm.rollNo} onChange={e => setSForm({ ...sForm, rollNo: e.target.value })} placeholder="2024001" style={inp} /></div>
                <div><label style={lbl}>Parent Phone</label><input value={sForm.parentPhone} onChange={e => setSForm({ ...sForm, parentPhone: e.target.value })} placeholder="98765XXXXX" style={inp} /></div>
                <div><label style={lbl}>Parent Email</label><input value={sForm.parentEmail} onChange={e => setSForm({ ...sForm, parentEmail: e.target.value })} placeholder="parent@gmail.com" style={inp} /></div>
              </div>
              <button onClick={addStudent} disabled={saving} style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: '#E24B4A', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
                {saving ? 'Adding...' : '+ Student Add Karo'}
              </button>
            </div>

            <div style={{ background: '#fff', border: '1px solid #e8e6ff', borderRadius: 16, padding: '1.25rem' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#A32D2D', marginBottom: 14 }}>👥 Students List ({students.length})</div>
              {students.length === 0 && <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Koi student nahi mila</div>}
              {students.map(s => {
                const count = incidents.filter(i => i.students?.name === s.name).length
                return (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '0.5px solid #f0f0f0' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#222' }}>👤 {s.name}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>{s.class || ''} {s.roll_no ? `· Roll: ${s.roll_no}` : ''} {s.parent_phone ? `· 📞 ${s.parent_phone}` : ''}</div>
                    </div>
                    <span style={{ fontSize: 12, padding: '3px 12px', borderRadius: 20, background: count >= 3 ? '#FCEBEB' : '#EAF3DE', color: count >= 3 ? '#A32D2D' : '#3B6D11', fontWeight: 600 }}>
                      {count} incidents {count >= 3 ? '⚠️' : ''}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* REPORT TAB */}
        {tab === 'report' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#A32D2D' }}>📄 Monthly Report</div>
              <button onClick={printReport} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: '#E24B4A', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>🖨️ Print / Download PDF</button>
            </div>
            <div ref={printRef} style={{ background: '#fff', border: '1px solid #e8e6ff', borderRadius: 16, padding: '2rem' }}>
              <div style={{ textAlign: 'center', marginBottom: 24, borderBottom: '2px solid #E24B4A', paddingBottom: 16 }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#A32D2D' }}>🛡️ DisciplineTrack</div>
                <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}>College Discipline Report — {new Date().toLocaleDateString('hi-IN', { month: 'long', year: 'numeric' })}</div>
                <div style={{ fontSize: 13, color: '#888' }}>Generated by: {user?.user_metadata?.name || user?.email}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                {[['Total Incidents', incidents.length], ['Warning Students', warningStudents.length], ['High Severity', highInc.length]].map(([l, v]) => (
                  <div key={l} style={{ textAlign: 'center', background: '#fafafe', borderRadius: 10, padding: '12px' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#A32D2D' }}>{v}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>{l}</div>
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 10 }}>⚠️ Warning Zone Students (3+ incidents):</div>
              {warningStudents.length === 0 ? <div style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Koi student warning zone mein nahi hai</div> : warningStudents.map(([n, c]) => (
                <div key={n} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#FCEBEB', borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: '#A32D2D' }}>{n}</span><span>{c} incidents</span>
                </div>
              ))}

              <div style={{ fontSize: 13, fontWeight: 700, color: '#333', margin: '16px 0 10px' }}>📋 Recent Incidents:</div>
              {incidents.slice(0, 20).map(inc => (
                <div key={inc.id} style={{ padding: '8px 12px', borderLeft: '3px solid #E24B4A', marginBottom: 8, background: '#fafafe', borderRadius: '0 8px 8px 0', fontSize: 12 }}>
                  <div style={{ fontWeight: 600 }}>{inc.students?.name} {inc.students?.class ? `(${inc.students.class})` : ''} — {typeLabel(inc.incident_type)}</div>
                  <div style={{ color: '#555', marginTop: 2 }}>{inc.description}</div>
                  <div style={{ color: '#888', marginTop: 2 }}>{inc.action_taken} · {new Date(inc.created_at).toLocaleDateString('hi-IN')}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
function Footer() {
  return (
    <div style={{ textAlign: 'center', padding: '20px', marginTop: '20px', borderTop: '1px solid #e8e6ff', background: '#fff' }}>
      <div style={{ fontSize: 13, color: '#A32D2D', fontWeight: 600 }}>🛡️ DisciplineTrack</div>
      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Developed & Designed by</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginTop: 2 }}>Asst. Professor | Deptt. of Chemistry | Pradhanmantri College of Excellence, Swami Vivekanand Govt. College, Raisen</div>
      <div style={{ fontSize: 12, color: '#666' }}>Asst. Professor | Deptt. of Chemistry | Pradhanmantri College of Excellence, Swami Vivekanand Govt. College, Raisen</div>
    </div>
  )
}
const lbl = { fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 5 }
const inp = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e0deff', fontSize: 13, color: '#222', background: '#fafafe', fontFamily: 'inherit', boxSizing: 'border-box' }
